<!--
  PolaroidStack — a lazy-loaded "stack of polaroids" preview for a gallery card.
  Shows up to 3 overlapping, slightly-rotated thumbnails so the shoot is
  recognizable at a glance. It only fetches when it scrolls into view (via
  IntersectionObserver) so a page of hundreds of galleries doesn't fire hundreds
  of requests on mount — this is the key to scaling the Galleries grid.

  Reuses the EXISTING GET /admin/galleries/{id} route (no backend change): its
  response .gallery.images[] carry `variants`, from which we derive a thumbnail
  URL with the same logic as GalleriesView's variantThumb helper.
-->
<template>
  <div ref="root" class="pstack" :class="{ 'is-empty': loaded && thumbs.length === 0 }">
    <!-- Loading placeholder: a single blank frame with a subtle shimmer -->
    <template v-if="!loaded">
      <div class="poly poly-skeleton" style="--i: 0"></div>
      <div class="poly poly-skeleton" style="--i: 1"></div>
      <div class="poly poly-skeleton" style="--i: 2"></div>
    </template>

    <!-- Loaded with images: up to 3 overlapping polaroids -->
    <template v-else-if="thumbs.length">
      <div
        v-for="(src, i) in thumbs"
        :key="i"
        class="poly"
        :style="{ '--i': i }"
      >
        <img :src="src" alt="" loading="lazy" draggable="false" />
      </div>
    </template>

    <!-- Loaded but empty: a single blank polaroid with a camera glyph -->
    <template v-else>
      <div class="poly poly-blank" style="--i: 1">
        <span class="poly-glyph" aria-hidden="true">📷</span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useAdminApi } from '@/composables/useAdminApi'

const props = defineProps<{ galleryId: number }>()
const { apiFetch } = useAdminApi()

const root = ref<HTMLElement | null>(null)
const loaded = ref(false)
const thumbs = ref<string[]>([])
let observer: IntersectionObserver | null = null
let fetched = false

// Same derivation as GalleriesView's variantThumb: prefer the '600' variant,
// then '900', then 'full', then the first available; build the CloudFront URL
// by stripping the leading `portfolio-media/` from the stored s3_key.
const MEDIA_PREFIX = '/portfolio-media/'
function variantThumb(img: any): string | undefined {
  const vs = img?.variants || []
  const v = vs.find((x: any) => x.label === '600') || vs.find((x: any) => x.label === '900')
        || vs.find((x: any) => x.label === 'full') || vs[0]
  return v ? MEDIA_PREFIX + String(v.s3_key).replace(/^portfolio-media\//, '') : undefined
}

async function fetchThumbs() {
  if (fetched) return
  fetched = true
  try {
    const res = await apiFetch(`/admin/galleries/${props.galleryId}`)
    const data = await res.json()
    const imgs: any[] = data?.gallery?.images || []
    thumbs.value = imgs
      .map(variantThumb)
      .filter((u): u is string => !!u)
      .slice(0, 3)
  } catch {
    // Leave thumbs empty; the empty state renders a blank polaroid.
  } finally {
    loaded.value = true
  }
}

onMounted(() => {
  // No IntersectionObserver (old/test env) → fetch immediately so it still works.
  if (typeof IntersectionObserver === 'undefined' || !root.value) {
    fetchThumbs()
    return
  }
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          fetchThumbs()
          observer?.disconnect()
          observer = null
          break
        }
      }
    },
    { rootMargin: '200px' }, // start fetching slightly before the card is visible
  )
  observer.observe(root.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<style scoped>
/* Container reserves a fixed, compact height; children are absolutely stacked. */
.pstack {
  position: relative;
  width: 100%;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* A single polaroid frame: white photo border, soft shadow, slight rotation
   that fans the three photos out into a loose stack. The white frame is an
   intentional photo-frame accent and reads fine over the dark card. */
.poly {
  position: absolute;
  width: 84px;
  height: 96px;
  background: #fff;
  padding: 5px 5px 16px;
  border-radius: 3px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  overflow: hidden;
}

/* Fan the stack: back photo rotated left + nudged, middle straight-ish,
   front rotated right. --i (0,1,2) drives per-photo transform + z-order. */
.poly:nth-child(1) { transform: translate(-20px, 2px) rotate(-6deg); z-index: 1; }
.poly:nth-child(2) { transform: translate(0, -2px)   rotate(3deg);  z-index: 2; }
.poly:nth-child(3) { transform: translate(20px, 3px) rotate(-2deg); z-index: 3; }

.poly img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 1px;
  background: var(--bg-elevated);
}

/* Loading skeleton: muted frame with a gentle pulse. */
.poly-skeleton {
  background: var(--bg-elevated);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35), 0 0 0 1px var(--border-subtle);
  animation: pstack-pulse 1.3s ease-in-out infinite;
  animation-delay: calc(var(--i) * 0.12s);
}

/* Empty state: one blank polaroid with a camera glyph. */
.pstack.is-empty { }
.poly-blank {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35), 0 0 0 1px var(--border-subtle);
}
.poly-glyph { font-size: 1.6rem; opacity: 0.5; line-height: 1; }

@keyframes pstack-pulse {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 0.9; }
}

@media (prefers-reduced-motion: reduce) {
  .poly-skeleton { animation: none; opacity: 0.7; }
}
</style>
